'use client'

import { ComponentProps } from 'react'

import { isButtonVariant } from '@syconium/little-miss-figgy/dist/components/Button/utils'
import { MediaAsContents } from '@syconium/little-miss-figgy/dist/components/Media'
import { mdEndPixels, pageLayoutMaxWidthPixels } from '@syconium/little-miss-figgy/dist/constants'
import { isUppercase } from '@syconium/little-miss-figgy/dist/lib/utils/isUppercase'

import { gql } from '../../../../../__generated__/graphql/catalog/gql'
import { PageHeroSectionFragment } from '../../../../../__generated__/graphql/catalog/graphql'
import { useStaticAmplitudeTracking } from '../../../../_hooks/useStaticTracking'
import { TrackingLocationProvider } from '../../../../_providers/TrackingLocationProvider.client'
import { useTranslation } from '../../../../_providers/TranslationProvider.client'
import { ClientOnlyResponsiveVideo } from '../../../media/ClientOnlyResponsiveVideo.client'
import { NextLink } from '../../../navigation/NextLink'
import { ContentfulRichText } from '../components/ContentfulRichText'

import {
  Container,
  CtaButton,
  Disclaimer,
  Eyebrow,
  HeroBottomMargin,
  HeroImage,
  ImageWrap,
  ImageWrapOverlay,
  StyledButtonBar,
  StyledButtonList,
  StyledButtonListItem,
  StyledScrollDownArrow,
  StyledWatchVideoDialog,
  Subtitle,
  TextBelowSection,
  TextWrap,
  TextWrapTitleImage,
  Title,
  TopArrowSpacer,
  VideoModalButtonWrapper,
} from './ContentfulPageHeroSection.styles'

gql(`
  fragment PageHeroSection on PageHeroSection {
    __typename
    id
    backgroundColor
    overlayColor
    buttonVariantDesktop
    buttonVariantMobile
    disclaimer
    description
    eyebrow
    hideDownArrow
    imageDesktop
    imageMobile
    video {
      inlineVideoDesktop
      inlineVideoMobile
      inlineVimeoIdDesktop
      inlineVimeoIdMobile
    }
    videoModal {
      modalVideoId
      modalVideoIdMobile
      openVideoModalText
    }
    isTitleSerif
    isTitleSerif
    isTitleItalic
    linkHref1
    linkHref2
    linkText1
    linkText2
    subtitle
    mobileTitleColor
    desktopTitleColor
    textContentMaxWidthDesktop
    textContentMaxWidthMobile
    textPlacementDesktop
    textPlacementMobile
    title
    titleImage
    titleImageAspectRatio
    titleImageMaxWidthDesktop
    titleImageMaxWidthMobile
    titleImageMobile
    titleImageMobileAspectRatio
    isFullscreenHeight
    hasVerticalMargin
    name
  }
`)

export type ContentfulPageHeroSectionProps = {
  pageSectionIndex: number
  data: PageHeroSectionFragment
  isCriticalImage?: boolean
}

export const ContentfulPageHeroSection = ({
  pageSectionIndex,
  isCriticalImage = pageSectionIndex < 2,
  data,
}: ContentfulPageHeroSectionProps) => {
  const translations = useTranslation()
  const section = data

  const {
    backgroundColor,
    buttonVariantDesktop,
    buttonVariantMobile,
    disclaimer,
    description,
    eyebrow,
    hideDownArrow,
    imageDesktop,
    imageMobile,
    video,
    videoModal,
    isTitleItalic,
    isTitleSerif,
    linkHref1,
    linkHref2,
    linkText1,
    linkText2,
    overlayColor,
    subtitle,
    mobileTitleColor,
    desktopTitleColor,
    textContentMaxWidthDesktop,
    textContentMaxWidthMobile,
    textPlacementDesktop,
    textPlacementMobile,
    title: propsTitle,
    titleImage,
    titleImageAspectRatio,
    titleImageMaxWidthDesktop,
    titleImageMaxWidthMobile,
    titleImageMobile,
    titleImageMobileAspectRatio,
    isFullscreenHeight,
    hasVerticalMargin,
  } = section

  const inlineVideoDesktop = video?.inlineVideoDesktop
  const inlineVideoMobile = video?.inlineVideoMobile
  const inlineVimeoIdDesktop = video?.inlineVimeoIdDesktop
  const inlineVimeoIdMobile = video?.inlineVimeoIdMobile
  const modalVideoId = videoModal?.modalVideoId
  const modalVideoIdMobile = videoModal?.modalVideoIdMobile
  const openVideoModalText = videoModal?.openVideoModalText
  const modalVideo = modalVideoId ? { id: modalVideoId, platform: 'vimeo' as const } : undefined
  const modalVideoMobile = modalVideoIdMobile
    ? { id: modalVideoIdMobile, platform: 'vimeo' as const }
    : undefined
  const defaultButtonVariant = 'black-on-white-no-border'
  const title = propsTitle
  const computedButtonVariantMobile = isButtonVariant(buttonVariantMobile)
    ? buttonVariantMobile
    : defaultButtonVariant
  const computedButtonVariantDesktop = isButtonVariant(buttonVariantDesktop)
    ? buttonVariantDesktop
    : defaultButtonVariant

  const DisclaimerText = (
    <Disclaimer
      $textColorMobile={mobileTitleColor}
      $textColorDesktop={desktopTitleColor}
      $placement={textPlacementDesktop}
    >
      <ContentfulRichText content={disclaimer} bodySize='BodyTiny' fontWeight='600' />
    </Disclaimer>
  )

  const HeroText = (
    <TextWrap
      $mobileTextColor={mobileTitleColor}
      $desktopTextColor={desktopTitleColor}
      $textPlacementMobile={textPlacementMobile}
      $textPlacementDesktop={textPlacementDesktop}
      $textContentMaxWidthDesktop={textContentMaxWidthDesktop}
      $textContentMaxWidthMobile={textContentMaxWidthMobile}
      $hasDisclaimer={!!disclaimer}
    >
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      {titleImage && (
        <TextWrapTitleImage
          $titleImageMaxWidthDesktop={titleImageMaxWidthDesktop}
          $titleImageMaxWidthMobile={titleImageMaxWidthMobile}
          alt={(title || description) ?? ''}
          src={titleImage}
          srcs={
            titleImageMobile
              ? {
                  sm: titleImageMobile,
                  md: titleImage,
                }
              : undefined
          }
          loading={isCriticalImage ? 'default' : 'lazy'}
          aspectRatios={{
            sm: titleImageMobile ? titleImageMobileAspectRatio : titleImageAspectRatio,
            md: titleImageAspectRatio,
          }}
          widths={{
            unit: 'px',
            sm: titleImageMaxWidthMobile ?? mdEndPixels,
            md: titleImageMaxWidthDesktop ?? pageLayoutMaxWidthPixels,
          }}
          transparentBackground
        />
      )}
      {title && (
        <Title
          {...{ $isTitleSerif: isTitleSerif, $isTitleItalic: isTitleItalic }}
          asTag='h3'
          isUppercase={isUppercase(title)}
        >
          {title}
        </Title>
      )}
      {subtitle && <Subtitle isUppercase={isUppercase(subtitle)}>{subtitle}</Subtitle>}
      <MediaAsContents lessThan='md'>
        {!!modalVideoMobile && (
          <VideoModalButtonWrapper>
            <StyledWatchVideoDialog
              videoId={modalVideoMobile.id}
              videoPlatform={modalVideoMobile.platform}
              text={openVideoModalText}
              textColor={mobileTitleColor}
            />
          </VideoModalButtonWrapper>
        )}
      </MediaAsContents>
      <MediaAsContents greaterThanOrEqual='md'>
        {!!modalVideo && (
          <VideoModalButtonWrapper>
            <StyledWatchVideoDialog
              videoId={modalVideo.id}
              videoPlatform={modalVideo.platform}
              text={openVideoModalText}
              textColor={desktopTitleColor}
            />
          </VideoModalButtonWrapper>
        )}
      </MediaAsContents>
      <StyledButtonBar>
        <StyledButtonList role='list'>
          {linkText1 && linkHref1 && (
            <StyledButtonListItem role='listitem'>
              <TrackingLocationProvider position={1}>
                <TrackedCtaButton
                  href={linkHref1}
                  variantDesktop={computedButtonVariantDesktop}
                  variantMobile={computedButtonVariantMobile}
                >
                  {linkText1}
                </TrackedCtaButton>
              </TrackingLocationProvider>
            </StyledButtonListItem>
          )}
          {linkText2 && linkHref2 && (
            <StyledButtonListItem role='listitem'>
              <TrackingLocationProvider position={2}>
                <TrackedCtaButton
                  href={linkHref2}
                  variantMobile={computedButtonVariantMobile}
                  variantDesktop={computedButtonVariantDesktop}
                >
                  {linkText2}
                </TrackedCtaButton>
              </TrackingLocationProvider>
            </StyledButtonListItem>
          )}
        </StyledButtonList>
      </StyledButtonBar>
      {textPlacementDesktop !== 'center' && DisclaimerText}
    </TextWrap>
  )

  return (
    <TrackingLocationProvider moduleName={section.name}>
      <Container
        $isFullscreenHeight={isFullscreenHeight}
        $textPlacementMobile={textPlacementMobile}
        $data-testid='page-hero-section-container'
      >
        {hideDownArrow !== true ? <TopArrowSpacer /> : null}
        <ImageWrap
          $backgroundColor={backgroundColor}
          $textPlacementMobile={textPlacementMobile}
          $shouldCollapse={!(imageMobile || inlineVideoMobile || !!modalVideo)}
        >
          <HeroImage
            imageDesktop={imageDesktop}
            imageMobile={imageMobile}
            alt={title}
            isCriticalImage={isCriticalImage}
          />
          <ClientOnlyResponsiveVideo
            inlineVideoDesktop={inlineVideoDesktop}
            inlineVideoMobile={inlineVideoMobile}
            inlineVimeoIdDesktop={inlineVimeoIdDesktop}
            inlineVimeoIdMobile={inlineVimeoIdMobile}
            playText={translations.magnolia.general.playVideo}
            pauseText={translations.magnolia.general.pauseVideo}
            objectPosition='center'
          />
          <ImageWrapOverlay overlayColor={overlayColor} />
        </ImageWrap>

        <MediaAsContents greaterThanOrEqual={textPlacementMobile === 'below' ? 'md' : 'xs'}>
          {HeroText}
        </MediaAsContents>

        {!hideDownArrow ? (
          <>
            <MediaAsContents lessThan='md'>
              <StyledScrollDownArrow
                $hideDownArrow={hideDownArrow}
                $textColor={mobileTitleColor}
                $textPlacementMobile={textPlacementMobile}
              />
            </MediaAsContents>

            <MediaAsContents greaterThanOrEqual='md'>
              <StyledScrollDownArrow
                $hideDownArrow={hideDownArrow}
                $textColor={desktopTitleColor}
                $textPlacementMobile={textPlacementMobile}
              />
            </MediaAsContents>
          </>
        ) : null}
        {textPlacementDesktop === 'center' && DisclaimerText}
      </Container>

      {textPlacementMobile === 'below' ? (
        <MediaAsContents lessThan='md'>
          <TextBelowSection>{HeroText}</TextBelowSection>
        </MediaAsContents>
      ) : null}
      {hasVerticalMargin && <HeroBottomMargin />}
    </TrackingLocationProvider>
  )
}

// Its own component so the event reads the CTA's TrackingLocationProvider.
const TrackedCtaButton = (props: ComponentProps<typeof CtaButton> & { href: string }) => {
  const { trackStaticAmplitudeEvent } = useStaticAmplitudeTracking()
  return (
    <CtaButton
      {...props}
      as={NextLink}
      onClick={() =>
        trackStaticAmplitudeEvent({ modifiedElement: 'Module Link', href: props.href })
      }
    />
  )
}
